"use client";

import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { Book, Circle } from "lucide-react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import { usePublicProfileStore } from "../store/usePublicProfileStore";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

// Import here at the bottom to avoid circular dependency

export function PublicAboutDisplay() {
  const { profileData } = usePublicProfileStore();
  const data = profileData?.about_section;
  const { t } = useTranslation(["profile"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  if (!data) return null;

  const sanitizedDescription = data.description
    ? DOMPurify.sanitize(data.description, {
        ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "h1", "h2", "ul", "ol", "li", "span"],
        ADD_TAGS: ["h1", "h2"],
        KEEP_CONTENT: true,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM: false,
        FORCE_BODY: false,
      })
    : null;

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-blue-400/50 shadow-2xl backdrop-blur-xl">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-15 -left-15 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-cyan-600" />
        <div className="absolute -right-15 -bottom-15 h-32 w-32 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500" />
      </div>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl bg-blue-500/20 p-2">
            <Icon
              className="text-blue-400"
              height="1.5rem"
              icon="mdi:about-circle-outline"
              width="1.5rem"
            />
          </div>
          {t("profile:aboutSection.title")}
        </motion.h2>
      </CardHeader>

      <CardContent className="space-y-6">
        {data.description && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="rounded-2xl border border-blue-300/20 bg-blue-500/10 p-4 backdrop-blur-sm"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <style>{`
      .wysiwyg-content {
        color: hsl(var(--secondary-foreground));
        font-size: 0.875rem;
        line-height: 1.5;
        text-wrap: wrap;
        break-words: break-word;
        min-height: 0 !important;
      }
      
      .wysiwyg-content h1 {
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
      }
      
      .wysiwyg-content h2 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }
      
      .wysiwyg-content h3 {
        font-size: 1.125rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }
      
      .wysiwyg-content p {
        margin-bottom: 0.5rem;
      }
      
      .wysiwyg-content ul,
      .wysiwyg-content ol {
        padding-left: 1.5rem;
        margin-bottom: 0.5rem;
      }
      
      .wysiwyg-content ul li {
        list-style-type: disc;
      }
      
      .wysiwyg-content ol li {
        list-style-type: decimal;
      }
      
      .wysiwyg-content blockquote {
        border-left: 3px solid hsl(var(--border));
        margin-left: 0;
        margin-right: 0;
        padding-left: 1rem;
        color: hsl(var(--muted-foreground));
      }
      
      .wysiwyg-content pre {
        background-color: hsl(var(--muted));
        border-radius: 0.25rem;
        padding: 0.75rem;
        font-family: monospace;
        overflow-x: auto;
        margin-bottom: 0.5rem;
      }
      
      .wysiwyg-content a {
        color: hsl(var(--primary));
        text-decoration: underline;
      }
    `}</style>
            <div
              dangerouslySetInnerHTML={{ __html: sanitizedDescription || "" }}
              className="wysiwyg-content"
            />
          </motion.div>
        )}

        {data.main_subjects && data.main_subjects.length > 0 && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="space-y-3"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="text-accent-foreground flex items-center gap-3">
              <div className="rounded-xl bg-blue-500/20 p-2">
                <Book className="h-5 w-5 text-blue-400" />
              </div>
              <h3 className="text-base font-medium">{t("profile:aboutSection.mainSubjects")}</h3>
            </div>
            <div className="flex flex-wrap gap-2 ps-8">
              {data.main_subjects.map(({ subject }, index) => (
                <div
                  key={subject.id}
                  className="flex items-center gap-2 rounded-xl border border-blue-400/30 bg-blue-500/20 px-3 py-2 text-blue-300 backdrop-blur-sm"
                >
                  <Icon className="flex h-4 w-4 items-center" icon={subject.icon} />
                  <span className="font-medium">{subject.name}</span>
                  {index !== data.main_subjects.length - 1 && (
                    <Circle className="ms-1 h-1.5 w-1.5" fill="currentColor" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
